import {ChangeDetectionStrategy, ChangeDetectorRef, Component, input} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {startWith, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {FormArray, FormGroup} from "@angular/forms";
import {BeslutningSkjemaKomponent} from "../beslutning-skjema/beslutning-skjema";
import {BeslutningSpesifikasjon, GruppeId} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erTomListe, nullSikkerListe, siste} from "felles-ng2/støttefunksjoner/lister";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {TranslateModule} from "@ngx-translate/core";
import {oppdaterGuiVedEndringIFormArrayLengde} from "felles-ng2/infrastruktur/angular/reactiveForm";


const debug = initDebugger('NyeBeslutningerSkjemaKomponent');

export type SkjemaType = ReturnType<typeof NyeBeslutningerSkjemaKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];
export type SkjemaRadType = ReturnType<typeof NyeBeslutningerSkjemaKomponent.opprettBeslutningSkjema>;
export type SkjemaRadVerdier = SkjemaRadType['value'];

/**
 * Skjema for å legge til nye beslutninger
 */
@Component({
    selector: 'nye-beslutninger-skjema',
    templateUrl: 'nye-beslutninger-skjema.html',
    styleUrl: 'nye-beslutninger-skjema.scss',
    host: {'data-testid': 'nye-beslutninger-skjema'},
    imports: [
        BeslutningSkjemaKomponent,
        MatIconButton,
        MatIcon,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NyeBeslutningerSkjemaKomponent {

    skjema = input.required<SkjemaType>();
    gruppeId = input.required<GruppeId>();

    static opprettSkjema() {
        return new FormArray<SkjemaRadType>([]);
    }

    /** Oppretter nytt skjema for beslutning */
    static opprettBeslutningSkjema() {
        let skjema = new FormGroup({
            beslutningskjema: BeslutningSkjemaKomponent.opprettSkjema()
        });
        BeslutningSkjemaKomponent.settFelterPåkrevdForNyBeslutning(skjema.controls.beslutningskjema);
        return skjema;
    }

    static somSpesifikasjoner(verdier: SkjemaVerdier): BeslutningSpesifikasjon[] {
        return nullSikkerListe(verdier)
            .filter(beslutning => erIkkeTomRad(beslutning))
            .map(beslutning => beslutningSomSpesifikasjon(beslutning));
    }

    constructor(cdRef: ChangeDetectorRef) {
        const skjemaSomObservable = toObservable(this.skjema);

        //FormArray.push (og øvrige metoder på FormArray) trigger ikke rendering, trigger derfor her manuelt oppdatering av GUI når antall beslutninger endres
        oppdaterGuiVedEndringIFormArrayLengde(skjemaSomObservable, cdRef);

        //sørger for at det vises en tom beslutning-rad under nye beslutninger initielt
        skjemaSomObservable
            .pipe(takeUntilDestroyed())
            .subscribe(skjema => this.leggTilNyBeslutning());

        //sørger for å legge til ny tom beslutning-rad under nye beslutninger når siste rad fylles ut
        skjemaSomObservable
            .pipe(
                switchMap(skjema => skjema.valueChanges.pipe(startWith(skjema.value))),
                takeUntilDestroyed())
            .subscribe(nye => this.vedEndringINyeBeslutninger(nye!));
    }

    slettNyBeslutning(indeks: number) {
        this.skjema().removeAt(indeks);
    }

    //private hjelpemetoder

    /** Legger til ny beslutningrad når siste rad fylles ut. */
    private vedEndringINyeBeslutninger(beslutninger: SkjemaRadVerdier[]) {
        //sørger for at det alltid vises en tom rad til slutt i listen
        this.leggTilNyBeslutningDersomSisteRadIkkeTom(beslutninger);

        //sørger for at det ikke vises mer enn en tom rad i slutten av listen
        if (beslutninger.slice(-2).filter(rad => erTomRad(rad)).length > 1) //sann dersom de to siste radene er tomme
            // fjerner nest siste rad (ikke siste - denne fungere som "trigger" for nye rader, dessuten måtte vi har fjernet required-validering som ligger nest siste om vi fjerner siste...)
            this.skjema().removeAt(this.skjema().length - 2);
    }

    private leggTilNyBeslutningDersomSisteRadIkkeTom(beslutninger: SkjemaRadVerdier[]) {
        if (erTomListe(beslutninger) || erIkkeTomRad(siste(beslutninger)))
            this.leggTilNyBeslutning();
    }

    /** Legger til ny beslutning sist i listen over nye beslutninger */
    private leggTilNyBeslutning(): SkjemaRadType {
        this.skjema().controls.forEach(rad => BeslutningSkjemaKomponent.settFelterPåkrevdForNyBeslutning(rad.controls.beslutningskjema));
        const nyBeslutning = NyeBeslutningerSkjemaKomponent.opprettBeslutningSkjema();
        //siste nye beslutning skal ikke ha validering (det skal alltid være et tom siste innslag i listen)
        BeslutningSkjemaKomponent.settFelterPåkrevdForNyBeslutning(nyBeslutning.controls.beslutningskjema, false);
        this.skjema().push(nyBeslutning);
        return nyBeslutning;
    }
}

function erIkkeTomRad(rad: SkjemaRadVerdier | null) {
    return !erTomRad(rad);
}

function erTomRad(rad: SkjemaRadVerdier | null) {
    return erTomEllerNil(rad?.beslutningskjema!.tittel) && erTomEllerNil(rad?.beslutningskjema!.beskrivelse);
}

function beslutningSomSpesifikasjon(beslutning: SkjemaRadVerdier): BeslutningSpesifikasjon {
    return {
        id: beslutning.beslutningskjema!.id!,
        slettet: false,
        tittel: beslutning.beslutningskjema!.tittel!,
        beskrivelse: beslutning.beslutningskjema!.beskrivelse!,
        oppgaver: {
            eksisterendeOppgaver: [],
            nyeOppgaver: []
        }
    };
}
